import { useState } from "react";
import classnames from "classnames";
import { Close, Search } from "@mui/icons-material";
import { CircularProgress, IconButton, Link, Stack, Typography } from "@mui/material";
import { makeStyles } from "components/providers/makeStyles";
import { useTranslate } from "components/providers/LocalizationProvider";

import { Input } from "../layout/forms/fields/Input";
import { trackClick, trackingCategory } from "utils/tracking";

const useStyles = makeStyles((theme, { inHeading }: { inHeading?: boolean }) => ({
    inputArea: {
        "& > div": {
            height: "100%",
            "& > label": {
                background: "none",
                padding: theme.spacing(1)
            },
            "& > input": {
                paddingLeft: 0,
            }
        },
        margin: inHeading ? theme.spacing(2, "auto") : undefined,
        maxWidth: inHeading ? theme.shape.pageMaxWidth : undefined,
    },
    closeIcon: {
        cursor: "pointer"
    }
}));

type SearchFormProps = {
    inputRef?: React.RefObject<HTMLInputElement | null>;
    searchFormRef?: React.RefObject<HTMLInputElement | null>;
    name?: string;
    search: string | undefined;
    onChange: (value: string, event: React.ChangeEvent<HTMLInputElement>) => void;
    loading?: boolean;
    inHeading?: boolean;
    className?: string;
    defaultValue?: string;
    placeholder?: string;
} & Omit<React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, "onChange">

export const SearchForm: React.FC<SearchFormProps> = ({ inputRef, searchFormRef, name, search, onChange, loading, inHeading, className, defaultValue, placeholder, ...rest }) => {
    const [renderClearButton, setRenderClearButton] = useState(!!(search || defaultValue));

    const onInputChange: React.ChangeEventHandler<HTMLInputElement> = e => {
        onChange(e.target.value, e);
        setRenderClearButton(!!e.target.value);
    };

    const clearInput: React.MouseEventHandler<HTMLButtonElement> = e => {
        onInputChange({ ...e, target: { value: "" } } as unknown as React.ChangeEvent<HTMLInputElement>);
        if (inputRef?.current) {
            inputRef.current.value = "";
            inputRef.current.focus();
        }
    };

    const classes = useStyles({ inHeading });


    return (
        <div className={classnames(classes.inputArea, className)} ref={searchFormRef}>
            {/* @ts-expect-error JS component complaining about optional props */}
            <Input field={{
                name,
                startAdornment: <Search />,
                endAdornment: loading
                    ? <Stack padding={1}><CircularProgress size={20}/></Stack>
                    : renderClearButton ? <IconButton size="small" onClick={clearInput}><Close className={classes.closeIcon}/></IconButton> : null,
                inputProps: {
                    ref: inputRef,
                    value: search,
                    type: "text",
                    onChange: onInputChange,
                    onClick: trackClick("Event & org search input", trackingCategory.searchEventAndOrganisation),
                    defaultValue,
                    placeholder: useTranslate(placeholder),
                    ...rest
                }
            }} />
        </div>
    );
};

const useNoResultsStyles = makeStyles((theme, { restrictMaxWidth }: { restrictMaxWidth: boolean }) => ({
    wrapper: {
        margin: theme.spacing(2, "auto"),
        maxWidth: restrictMaxWidth ? theme.shape.pageMaxWidth : "inherit",
    },
    link: {
        marginTop: theme.spacing(1)
    }
}));

type NoSearchResultsProps = {
    className: string;
    restrictMaxWidth: boolean;
    noResultsText: string;
    clearResultsText: string;
    onClear: () => void;
};

export const NoSearchResults: React.FC<NoSearchResultsProps> = ({
    className,
    restrictMaxWidth = false,
    noResultsText,
    clearResultsText,
    onClear,
}) => {
    const classes = useNoResultsStyles({ restrictMaxWidth });

    return (
        <div className={classnames(classes.wrapper, className)}>
            <Typography variant="body1" component="p">{noResultsText}</Typography>
            <Typography variant="button2" component="p" className={classes.link}>
                <Link underline="always" onClick={onClear} role="button">{clearResultsText}</Link>
            </Typography>
        </div>
    );
};
